import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getOrderInsightsReport } from "../../redux/apiCalls";
import moment from "moment";
import "./reporting.css";
import { FiPlus } from "react-icons/fi";
import styled, { keyframes } from "styled-components";
import {
    MdAccessTime,
    MdQueryStats,
    MdTrendingUp,
    MdWarning,
} from "react-icons/md";
import { mobile, tablet } from "../../responsive";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const DashboardBg = styled.div`
    min-height: 100vh;
    background: #0b1220;
    color: #fff;
    padding-bottom: 3rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    width: 100%;
`;

const VideoBackground = styled.video`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -2;
    opacity: 1;
    pointer-events: none;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;

    @media (prefers-reduced-motion: reduce) {
        display: none;
    }
`;

const BackgroundOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.45);
    z-index: -1;
    pointer-events: none;
`;

const HudOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
`;

const DashboardContainer = styled.div`
    margin: 0 auto;
    padding: 2.5rem 2rem 4rem;
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 1s ease;
    max-width: 1400px;
    width: 100%;
    box-sizing: border-box;

    ${mobile({
        padding: "1.5rem 1rem 3rem",
    })}
`;

const PageHeader = styled.div`
    margin-bottom: 2rem;
    text-align: center;
`;

const PageTitle = styled.h1`
    font-size: 1.875rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    text-shadow: 0 4px 8px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.025em;
    background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const PageSubtitle = styled.p`
    font-size: 0.875rem;
    color: rgba(209, 213, 219, 0.95);
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
`;

const ChartSection = styled.section`
    background: rgba(17, 24, 39, 0.94);
    border-radius: 20px;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.45);
    padding: 1.85rem 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 1.75rem;

    ${mobile({
        padding: "1.4rem 1.05rem",
        marginBottom: "1.25rem",
    })}
`;

const GlassChartSection = styled(ChartSection)`
    background: rgba(20, 30, 50, 0.95);
    border-radius: 16px;
    box-shadow: 0 4px 32px 0 #00eaff22;
    padding: 1.5rem 1.2rem;
    border: 1.5px solid #2e3a5e;
    margin-bottom: 1rem;
`;

const ChartTitle = styled.h3`
    color: #ffffff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 700;
    font-size: 1.35rem;
    margin-bottom: 1.25rem;
    letter-spacing: 0.025em;
    position: relative;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);

    &::after {
        content: "";
        position: absolute;
        bottom: -0.6rem;
        left: 0;
        width: 3.25rem;
        height: 3px;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.9) 0%,
            rgba(255, 255, 255, 0.3) 100%
        );
        border-radius: 2px;
    }
`;

const BlueChartTitle = styled.h3`
    color: #00eaff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    letter-spacing: 0.025em;
`;

const SubText = styled.p`
    color: rgba(209, 213, 219, 0.85);
    font-size: 0.8rem;
    margin-top: -0.35rem;
    margin-bottom: 1rem;
    line-height: 1.55;

    strong {
        color: rgba(255, 255, 255, 0.92);
        font-weight: 600;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.75rem;

    ${tablet({
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "1.35rem",
    })}

    ${mobile({
        gridTemplateColumns: "1fr",
        gap: "1.1rem",
    })}
`;

const StatCard = styled.div`
    background: linear-gradient(
        135deg,
        rgba(37, 99, 235, 0.16) 0%,
        rgba(15, 23, 42, 0.85) 100%
    );
    border-radius: 18px;
    padding: 1.35rem 1.25rem;
    border: 1px solid rgba(59, 130, 246, 0.25);
    position: relative;
    overflow: hidden;
`;

const StatTitle = styled.div`
    font-size: 0.82rem;
    font-weight: 600;
    color: rgba(226, 232, 240, 0.85);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
    font-size: 2rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.35rem;
    text-shadow: 0 0 12px rgba(79, 70, 229, 0.45);

    ${mobile({
        fontSize: "1.7rem",
    })}
`;

const StatDescription = styled.div`
    font-size: 0.85rem;
    color: rgba(226, 232, 240, 0.75);
`;

const StatIcon = styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.4rem;
    color: #38bdf8;
    opacity: 0.85;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;

    ${mobile({
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    })}
`;

const FilterCard = styled.div`
    background: rgba(15, 23, 42, 0.85);
    border-radius: 14px;
    border: 1.5px solid rgba(59, 130, 246, 0.2);
    padding: 1.2rem 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const FilterLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const FilterInput = styled.input`
    width: 100%;
    border-radius: 0.75rem;
    padding: 0.65rem 0.9rem;
    background: rgba(30, 41, 59, 0.85);
    border: 1px solid rgba(148, 163, 184, 0.45);
    color: #f8fafc;
    font-size: 0.95rem;

    &:focus {
        border-color: rgba(59, 130, 246, 0.75);
        outline: none;
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.85rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 1rem;

    ${mobile({
        flexDirection: "column",
        alignItems: "stretch",
    })}
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    border-radius: 9999px;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    color: #ffffff;
    background: linear-gradient(135deg, #ef4444, #f97316);
    box-shadow: 0 10px 25px rgba(239, 68, 68, 0.35);

    &:hover {
        transform: translateY(-2px);
    }
`;

const LuxuryTable = styled.div`
    background: linear-gradient(
        135deg,
        rgba(15, 23, 42, 0.92) 0%,
        rgba(30, 41, 59, 0.92) 100%
    );
    border-radius: 20px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.15);
    overflow: hidden;
`;

const GlassTableScroll = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const GlassTable = styled.table`
    width: 100%;
    min-width: 960px;
    border-collapse: separate;
    border-spacing: 0;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
`;

const GlassTh = styled.th`
    text-align: left;
    padding: 0.9rem 1.25rem;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #a5f3fc;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.98) 0%,
        rgba(30, 41, 59, 0.92) 100%
    );
    border-bottom: 2px solid rgba(34, 211, 238, 0.45);
`;

const GlassTd = styled.td`
    padding: 0.95rem 1.25rem;
    font-size: 0.875rem;
    color: rgba(248, 250, 252, 0.95);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const GlassTdLead = styled(GlassTd)`
    font-weight: 700;
    color: #fef3c7;
`;

const GlassTdWarning = styled(GlassTd)`
    color: #fca5a5;
    font-weight: 600;
`;

const GlassTr = styled.tr`
    &:nth-child(even) td {
        background: rgba(15, 23, 42, 0.45);
    }
    &:nth-child(odd) td {
        background: rgba(15, 23, 42, 0.2);
    }
    &:hover td {
        background: rgba(34, 211, 238, 0.12) !important;
    }
`;

const GlassTbody = styled.tbody`
    tr:last-child td {
        border-bottom: none;
    }
`;

const TableEmptyState = styled.div`
    padding: 2rem 1.5rem;
    text-align: center;
    font-size: 0.875rem;
    color: rgba(156, 163, 175, 0.95);
`;

const InvoiceLink = styled(Link)`
    color: #67e8f9;
    text-decoration: none;
    font-weight: 600;

    &:hover {
        color: #a5f3fc;
        text-decoration: underline;
    }
`;

const EMPTY = "—";

const capitalizeWords = (value) =>
    String(value)
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCustomerName = (value) => {
    if (!value) return EMPTY;
    return capitalizeWords(value);
};

const formatStatus = (value) => {
    if (!value) return EMPTY;
    const spaced = String(value).replace(/([a-z])([A-Z])/g, "$1 $2");
    return capitalizeWords(spaced);
};

export default function OrderInsightsReport() {
    const currentUser = useSelector((state) => state.user.currentUser);
    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );
    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );

    const TwoDecimalFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const IntegerFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const currencySymbol =
        process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";
    const formatCurrency = (value) =>
        `${currencySymbol}${TwoDecimalFormatter.format(value || 0)}`;

    const [reportLoading, setReportLoading] = useState(false);
    const [insightsReport, setInsightsReport] = useState({
        expenseThresholdPercent: 40,
        longestCompletionOrders: [],
        highExpenseOrders: [],
        summary: {
            longestCompletionCount: 0,
            longestCompletionDays: 0,
            highExpenseCount: 0,
        },
    });

    const [inputs, setInputs] = useState({
        principle: principle
            ? principle
            : superadmin
            ? undefined
            : currentUser?._id,
        dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
    });

    const handleDateFrom = (e) => {
        setInputs((prev) => ({ ...prev, dateFrom: e.target.value }));
    };

    const handleDateTo = (e) => {
        setInputs((prev) => ({ ...prev, dateTo: e.target.value }));
    };

    const handleResetFilters = () => {
        setInputs({
            principle: principle
                ? principle
                : superadmin
                ? undefined
                : currentUser?._id,
            dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
            dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            setReportLoading(true);
            const res = await getOrderInsightsReport(inputs);
            setInsightsReport(
                res || {
                    expenseThresholdPercent: 40,
                    longestCompletionOrders: [],
                    highExpenseOrders: [],
                    summary: {
                        longestCompletionCount: 0,
                        longestCompletionDays: 0,
                        highExpenseCount: 0,
                    },
                }
            );
            setReportLoading(false);
        };
        fetchData();
    }, [inputs]);

    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                video.pause();
                return;
            }
            video.play().catch(() => {});
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    const summaryStats = [
        {
            title: "Longest Completion",
            value: `${IntegerFormatter.format(
                insightsReport.summary?.longestCompletionDays || 0
            )} days`,
            description: "Highest completion time in the top 20 list",
            icon: <MdAccessTime />,
        },
        {
            title: "Top Completion Orders",
            value: IntegerFormatter.format(
                insightsReport.summary?.longestCompletionCount || 0
            ),
            description: "Orders shown in the longest completion table",
            icon: <MdQueryStats />,
        },
        {
            title: "High Expense Orders",
            value: IntegerFormatter.format(
                insightsReport.summary?.highExpenseCount || 0
            ),
            description: `Orders at or above ${insightsReport.expenseThresholdPercent || 40}% expense ratio`,
            icon: <MdWarning />,
        },
        {
            title: "Expense Threshold",
            value: `${insightsReport.expenseThresholdPercent || 40}%`,
            description: "Expenses as a share of order total amount",
            icon: <MdTrendingUp />,
        },
    ];

    const formatDate = (value) =>
        value ? moment(value).format("D MMM YYYY") : EMPTY;

    const renderCompletionTable = () => (
        <GlassTableScroll>
            <GlassTable>
                <thead>
                    <tr>
                        <GlassTh>#</GlassTh>
                        <GlassTh>Invoice</GlassTh>
                        <GlassTh>CO Number</GlassTh>
                        <GlassTh>Customer</GlassTh>
                        <GlassTh>Outlet</GlassTh>
                        <GlassTh>Salesman</GlassTh>
                        <GlassTh>Total Amount</GlassTh>
                        <GlassTh>Completion Days</GlassTh>
                        <GlassTh>Status</GlassTh>
                        <GlassTh>Created</GlassTh>
                    </tr>
                </thead>
                <GlassTbody>
                    {(insightsReport.longestCompletionOrders || []).map(
                        (row, index) => (
                            <GlassTr key={row.id || row.invoiceId}>
                                <GlassTd>{index + 1}</GlassTd>
                                <GlassTd>
                                    <InvoiceLink to={`/order/${row.id}`}>
                                        {row.invoiceId || EMPTY}
                                    </InvoiceLink>
                                </GlassTd>
                                <GlassTd>{row.coNumber || EMPTY}</GlassTd>
                                <GlassTdLead>
                                    {formatCustomerName(row.customerName)}
                                </GlassTdLead>
                                <GlassTd>{row.outlet || EMPTY}</GlassTd>
                                <GlassTd>{row.salesman || EMPTY}</GlassTd>
                                <GlassTd>
                                    {formatCurrency(row.totalAmount || 0)}
                                </GlassTd>
                                <GlassTdLead>
                                    {IntegerFormatter.format(
                                        row.completionDays || 0
                                    )}{" "}
                                    days
                                </GlassTdLead>
                                <GlassTd>{formatStatus(row.status)}</GlassTd>
                                <GlassTd>{formatDate(row.createdAt)}</GlassTd>
                            </GlassTr>
                        )
                    )}
                </GlassTbody>
            </GlassTable>
        </GlassTableScroll>
    );

    const renderHighExpenseTable = () => (
        <GlassTableScroll>
            <GlassTable>
                <thead>
                    <tr>
                        <GlassTh>Invoice</GlassTh>
                        <GlassTh>CO Number</GlassTh>
                        <GlassTh>Customer</GlassTh>
                        <GlassTh>Outlet</GlassTh>
                        <GlassTh>Salesman</GlassTh>
                        <GlassTh>Total Amount</GlassTh>
                        <GlassTh>Total Expenses</GlassTh>
                        <GlassTh>Expense Ratio</GlassTh>
                        <GlassTh>Completion Days</GlassTh>
                        <GlassTh>Status</GlassTh>
                    </tr>
                </thead>
                <GlassTbody>
                    {(insightsReport.highExpenseOrders || []).map((row) => (
                        <GlassTr key={row.id || row.invoiceId}>
                            <GlassTd>
                                <InvoiceLink to={`/order/${row.id}`}>
                                    {row.invoiceId || EMPTY}
                                </InvoiceLink>
                            </GlassTd>
                            <GlassTd>{row.coNumber || EMPTY}</GlassTd>
                            <GlassTdLead>
                                {formatCustomerName(row.customerName)}
                            </GlassTdLead>
                            <GlassTd>{row.outlet || EMPTY}</GlassTd>
                            <GlassTd>{row.salesman || EMPTY}</GlassTd>
                            <GlassTd>
                                {formatCurrency(row.totalAmount || 0)}
                            </GlassTd>
                            <GlassTd>
                                {formatCurrency(row.totalExpenses || 0)}
                            </GlassTd>
                            <GlassTdWarning>
                                {(row.expenseRatio || 0).toFixed(1)}%
                            </GlassTdWarning>
                            <GlassTd>
                                {row.completionDays
                                    ? `${IntegerFormatter.format(row.completionDays)} days`
                                    : EMPTY}
                            </GlassTd>
                            <GlassTd>{formatStatus(row.status)}</GlassTd>
                        </GlassTr>
                    ))}
                </GlassTbody>
            </GlassTable>
        </GlassTableScroll>
    );

    return (
        <DashboardBg>
            <VideoBackground
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
            >
                <source
                    src="https://ik.imagekit.io/dnddecpho/Inhome/animatedbg_Rx2Blqd5L.mp4?updatedAt=1746618975517"
                    type="video/mp4"
                />
            </VideoBackground>
            <BackgroundOverlay />
            <HudOverlay />
            <DashboardContainer>
                <PageHeader>
                    <PageTitle>Order Insights Report</PageTitle>
                    <PageSubtitle>
                        Longest completion orders and high expense ratio analysis
                    </PageSubtitle>
                </PageHeader>

                <ChartSection>
                    <BlueChartTitle>Filters</BlueChartTitle>
                    <FiltersGrid>
                        <FilterCard>
                            <FilterLabel htmlFor="dateFrom">Date From</FilterLabel>
                            <FilterInput
                                id="dateFrom"
                                name="dateFrom"
                                type="date"
                                value={inputs.dateFrom}
                                onChange={handleDateFrom}
                            />
                        </FilterCard>
                        <FilterCard>
                            <FilterLabel htmlFor="dateTo">Date To</FilterLabel>
                            <FilterInput
                                id="dateTo"
                                name="dateTo"
                                type="date"
                                value={inputs.dateTo}
                                onChange={handleDateTo}
                            />
                        </FilterCard>
                    </FiltersGrid>
                    <ActionButtons>
                        <ActionButton type="button" onClick={handleResetFilters}>
                            <FiPlus />
                            Reset Filters
                        </ActionButton>
                    </ActionButtons>
                </ChartSection>

                <ChartSection>
                    <ChartTitle>Summary Overview</ChartTitle>
                    <StatsGrid>
                        {summaryStats.map((stat) => (
                            <StatCard key={stat.title}>
                                <StatIcon>{stat.icon}</StatIcon>
                                <StatTitle>{stat.title}</StatTitle>
                                <StatValue>{stat.value}</StatValue>
                                {stat.description && (
                                    <StatDescription>{stat.description}</StatDescription>
                                )}
                            </StatCard>
                        ))}
                    </StatsGrid>
                </ChartSection>

                <GlassChartSection>
                    <BlueChartTitle>Longest 20 Completion Orders</BlueChartTitle>
                    <SubText>
                        Orders with the highest <strong>completionFromApproval</strong>{" "}
                        days in the selected date range, sorted longest first.
                    </SubText>
                    <LuxuryTable>
                        {reportLoading ? (
                            <TableEmptyState>Loading completion data…</TableEmptyState>
                        ) : !(insightsReport.longestCompletionOrders || []).length ? (
                            <TableEmptyState>
                                No completed orders with completion days found.
                            </TableEmptyState>
                        ) : (
                            renderCompletionTable()
                        )}
                    </LuxuryTable>
                </GlassChartSection>

                <GlassChartSection>
                    <BlueChartTitle>High Expense Orders</BlueChartTitle>
                    <SubText>
                        All orders where total expenses are{" "}
                        <strong>{insightsReport.expenseThresholdPercent || 40}%</strong> or
                        above of total order amount (
                        <strong>amount − discount</strong>). Includes materials and
                        expense fields.
                    </SubText>
                    <LuxuryTable>
                        {reportLoading ? (
                            <TableEmptyState>Loading expense data…</TableEmptyState>
                        ) : !(insightsReport.highExpenseOrders || []).length ? (
                            <TableEmptyState>
                                No orders meet the expense threshold for this period.
                            </TableEmptyState>
                        ) : (
                            renderHighExpenseTable()
                        )}
                    </LuxuryTable>
                </GlassChartSection>
            </DashboardContainer>
        </DashboardBg>
    );
}
